import type { CumulativeReport } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { CompletenessBanner } from '../components/CompletenessBanner.js';
import { AUDIT_TABS, PageTabs } from '../components/PageTabs.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import {
  Button,
  Card,
  EstimateBanner,
  EstimatedBadge,
  Field,
  InfoTip,
  Loading,
  SortHeader,
  Spinner,
  inputClass,
  useSort,
} from '../components/ui.js';
import { downloadCsv, toCsv } from '../lib/csv.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * Cumulative audit view — the figures an agency takes to a carrier audit. Sums
 * commission owed / at-risk / chargebacks over a period RANGE (reads the fast D1
 * projection via GET /reports/cumulative), with month-by-month and per-carrier
 * breakdowns + CSV export. Distinct from Compare (a two-month member-level diff).
 */
interface Scorecard {
  carrierId: string;
  carrierName: string | null;
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  consistencyPct: number;
  periodsReported: number;
  periodsExpected: number;
  qualityFlags: number;
  commissionOwed: number;
  owedPerMemberMonth: number;
  chargebackAmount: number;
  dropRatePct: number;
  avgMembers: number;
}

export function Audit() {
  const { client, baseUrl, token } = useAuth();
  const { carriers } = useCarriers();
  const [scorecards, setScorecards] = useState<Scorecard[] | null>(null);
  const [periods, setPeriods] = useState<string[]>([]);
  const [carrierId, setCarrierId] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [report, setReport] = useState<CumulativeReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Discover carriers + the periods that have data; default to the FULL book
  // (all periods) — the natural starting point for an audit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bootstrap once
  useEffect(() => {
    client
      .attritionSeries({ months: 36 })
      .then((r) => {
        const ps = r.data.map((d) => d.period);
        setPeriods(ps);
        if (ps.length > 0) {
          setFrom(ps[0] ?? '');
          setTo(ps[ps.length - 1] ?? '');
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setBusy(true);
    setError(null);
    client
      .cumulative({
        from: from || undefined,
        to: to || undefined,
        carrierId: carrierId || undefined,
        workspaceId: workspaceId || undefined,
      })
      .then((res) => active && setReport(res))
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [client, from, to, carrierId, workspaceId]);

  // Carrier scorecards — cross-carrier ranking over the same range (off the SDK,
  // direct fetch). Carrier filter doesn't apply (it's inherently a multi-carrier view).
  useEffect(() => {
    let active = true;
    setScorecards(null);
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/carrier-scorecards?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => active && b && setScorecards(b.carriers))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, from, to, workspaceId]);

  const setPreset = (monthsBack: number | 'all' | 'ytd') => {
    if (periods.length === 0) return;
    const latest = periods[periods.length - 1] ?? '';
    setTo(latest);
    if (monthsBack === 'all') {
      setFrom(periods[0] ?? '');
    } else if (monthsBack === 'ytd') {
      const year = latest.slice(0, 4);
      const jan = `${year}-01`;
      setFrom(periods.find((p) => p >= jan) ?? periods[0] ?? '');
    } else {
      const idx = periods.length - 1 - monthsBack;
      setFrom(periods[idx >= 0 ? idx : 0] ?? '');
    }
  };

  const carrierName = carrierId
    ? (carriers.find((c) => c.id === carrierId)?.name ?? '')
    : 'all carriers';

  const exportCsv = () => {
    if (!report) return;
    const csv = toCsv(report.byPeriod, [
      { key: 'period', header: 'Period' },
      { key: 'memberCount', header: 'Members' },
      { key: 'commissionOwed', header: 'Owed' },
      { key: 'commissionAtRisk', header: 'At risk' },
      { key: 'chargebackAmount', header: 'Chargebacks' },
      { key: 'owedEvaluated', header: 'Owed evaluated' },
      { key: 'owedTotal', header: 'Owed total' },
      { key: 'red', header: 'Dropped' },
      { key: 'new', header: 'New' },
      { key: 'reappeared', header: 'Reappeared' },
    ]);
    downloadCsv(`audit-${report.range.from ?? 'all'}-to-${report.range.to ?? 'all'}.csv`, csv);
  };

  const t = report?.totals;
  // Sortable tables (hooks must run unconditionally → default to []).
  const periodSort = useSort(report?.byPeriod ?? [], { key: 'period', dir: 'asc' });
  const carrierSort = useSort(report?.byCarrier ?? [], { key: 'commissionOwed', dir: 'desc' });
  const rangeLabel = useMemo(() => {
    if (!report?.range.from || !report.range.to) return 'no data';
    return report.range.from === report.range.to
      ? formatPeriodLong(report.range.from)
      : `${formatPeriodLong(report.range.from)} → ${formatPeriodLong(report.range.to)}`;
  }, [report]);

  return (
    <div className="space-y-6">
      <PageTabs tabs={AUDIT_TABS} />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit &amp; cumulative totals</h1>
        <p className="text-sm text-neutral-500">
          Commission owed, at-risk, and chargebacks summed across a date range — month by month and
          per carrier. The recoverable picture to take to a carrier audit.
        </p>
      </div>

      <CompletenessBanner
        carrierId={carrierId || undefined}
        workspaceId={workspaceId || undefined}
      />

      <Card>
        <div className="flex flex-col gap-4">
          <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
          <div className="grid items-end gap-4 md:grid-cols-3">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
              >
                <option value="">All carriers</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="From">
              <PeriodPicker value={from} onChange={setFrom} />
            </Field>
            <Field label="To">
              <PeriodPicker value={to} onChange={setTo} />
            </Field>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-400">Quick range:</span>
          <Preset label="All time" onClick={() => setPreset('all')} />
          <Preset label="Year to date" onClick={() => setPreset('ytd')} />
          <Preset label="Last 12 mo" onClick={() => setPreset(11)} />
          <Preset label="Last 24 mo" onClick={() => setPreset(23)} />
        </div>
      </Card>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      <p className="text-sm text-neutral-500">
        <span className="font-medium text-neutral-800 dark:text-neutral-200">{rangeLabel}</span>
        {report ? ` · ${report.range.periodsCovered} period(s)` : ''} for{' '}
        <span className="font-medium text-neutral-800 dark:text-neutral-200">{carrierName}</span>
        {busy && (
          <span className="ml-2 inline-flex items-center align-middle text-emerald-600">
            <Spinner className="h-3.5 w-3.5" />
          </span>
        )}
      </p>

      {busy && !report && (
        <Card>
          <Loading label="Summing the audit window…" />
        </Card>
      )}

      {t?.owedEstimated && <EstimateBanner />}

      {t && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <BigStat
            label="Commission owed"
            value={formatCurrency(t.commissionOwed)}
            tone="emerald"
            badge={t.owedEstimated ? <EstimatedBadge /> : undefined}
            note={
              t.owedEstimated
                ? 'includes estimated rates'
                : `${pct(t.owedCoverage)} of the book priced`
            }
            info={
              <>
                Expected − actual commission (your contracted rate minus what the carrier paid),
                summed over the range — the recoverable shortfall. Each month's figure is that
                month's expected-minus-actual, so adding them is the true total owed.{' '}
                <b>Only policies with a configured rate are priced</b> (see Owed coverage); the rest
                are processed but not yet in this number — so it can only go up.
                {t.owedEstimated && ' Some carriers are still on an auto-seeded estimate rate.'}
              </>
            }
          />
          <BigStat
            label="Commission at risk"
            value={formatCurrency(t.commissionAtRisk)}
            tone="amber"
            note="month-over-month shortfall"
            info={
              <>
                Month-over-month variance flagged for review, summed over the range: the prior
                commission of <b>dropped</b> members plus the <b>decrease</b> for members paid less
                than the month before. A “look here” signal, <b>not</b> a final owed amount — it
                needs no rate setup.
              </>
            }
          />
          <BigStat
            label="Chargebacks"
            value={`-${formatCurrency(t.chargebackAmount)}`}
            tone="rose"
            note={`${t.chargebackCount.toLocaleString()} clawed back`}
            info={
              <>
                Total commission the carriers <b>clawed back</b> over the range (negative-amount
                rows), and how many. Tracked per policy with a link to the original payout on the{' '}
                <Link to="/chargebacks" className="text-emerald-600 hover:underline">
                  Chargebacks
                </Link>{' '}
                page.
              </>
            }
          />
          <BigStat
            label="Owed coverage"
            value={pct(t.owedCoverage)}
            note={`${t.owedEvaluated.toLocaleString()} / ${t.owedTotal.toLocaleString()} records priced`}
            info={
              <>
                The share of your book the <b>owed</b> figure could be <b>priced</b> on — policies
                with a configured rate (and a premium, for % rates). <b>Not</b> “% of the book
                processed”: the whole book is always ingested and scored. The rest are held out of
                owed (never counted as $0). <b>To raise it, add the missing rate cards</b> on the{' '}
                <Link to="/rates" className="text-emerald-600 hover:underline">
                  Rates
                </Link>{' '}
                page — owed coverage climbs as more products/states get a rate.
              </>
            }
          />
        </div>
      )}

      {t && (
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4 lg:grid-cols-6">
          <MiniStat
            label="Avg members / mo"
            value={t.avgMembers.toLocaleString()}
            info="Mean distinct members per month over the range. Use this (not member-months) for the size of the book."
          />
          <MiniStat
            label="Peak members"
            value={t.peakMembers.toLocaleString()}
            info="The largest single-month member count in the range."
          />
          <MiniStat
            label="Member-months"
            value={t.memberMonths.toLocaleString()}
            info="The sum of each month's member count — a volume, NOT a head-count of distinct people (a 12-month, ~1,150-member book is ~13,800 member-months)."
          />
          <MiniStat
            label="Dropped (total)"
            value={t.red.toLocaleString()}
            info="Total red (dropped) member-periods across the range — present one month, absent the next. Counts fallout each month, so a member who drops twice counts twice."
          />
          <MiniStat
            label="New (total)"
            value={t.new.toLocaleString()}
            info="Total first-time-seen members across the range (the NEW flag)."
          />
          <MiniStat
            label="Reappeared"
            value={t.reappeared.toLocaleString()}
            info="Total members who returned after being absent (the REAPPEARED flag) across the range."
          />
        </div>
      )}

      {report && report.byCarrier.length > 1 && (
        <Card className="p-0">
          <div className="border-b border-neutral-200 px-5 py-3 text-sm font-semibold dark:border-neutral-800">
            By carrier
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-neutral-500">
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  {(() => {
                    const sp = {
                      sortKey: carrierSort.sortKey,
                      sortDir: carrierSort.sortDir,
                      onSort: carrierSort.toggle,
                    };
                    return (
                      <>
                        <SortHeader label="Carrier" k="carrierName" {...sp} className="px-5 py-2" />
                        <SortHeader
                          label="Owed"
                          k="commissionOwed"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="At risk"
                          k="commissionAtRisk"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="Chargebacks"
                          k="chargebackAmount"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="Coverage"
                          k="owedCoverage"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="Periods"
                          k="periodsCovered"
                          align="right"
                          {...sp}
                          className="px-5 text-right"
                        />
                      </>
                    );
                  })()}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
                {carrierSort.sorted.map((c) => (
                  <tr key={c.carrierId}>
                    <td className="px-5 py-2 font-medium">
                      {c.carrierName ?? c.carrierId.slice(0, 8)}
                    </td>
                    <td className="px-3 text-right tabular-nums">
                      {formatCurrency(c.commissionOwed)}
                    </td>
                    <td className="px-3 text-right tabular-nums text-neutral-500">
                      {formatCurrency(c.commissionAtRisk)}
                    </td>
                    <td className="px-3 text-right tabular-nums text-neutral-500">
                      -{formatCurrency(c.chargebackAmount)}
                    </td>
                    <td className="px-3 text-right tabular-nums text-neutral-500">
                      {pct(c.owedCoverage)}
                    </td>
                    <td className="px-5 text-right tabular-nums text-neutral-500">
                      {c.periodsCovered}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {scorecards && scorecards.length > 1 && <CarrierScorecards rows={scorecards} />}

      {report && report.byPeriod.length > 0 && (
        <Card className="p-0">
          <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
            <span className="text-sm font-semibold">Month by month</span>
            <Button variant="ghost" onClick={exportCsv}>
              Export audit CSV
            </Button>
          </div>
          <div className="max-h-[55vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white text-left text-xs uppercase tracking-wide text-neutral-500 dark:bg-neutral-900">
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  {(() => {
                    const sp = {
                      sortKey: periodSort.sortKey,
                      sortDir: periodSort.sortDir,
                      onSort: periodSort.toggle,
                    };
                    return (
                      <>
                        <SortHeader label="Period" k="period" {...sp} className="px-5 py-2" />
                        <SortHeader
                          label="Members"
                          k="memberCount"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="Owed"
                          k="commissionOwed"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="At risk"
                          k="commissionAtRisk"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="Chargebacks"
                          k="chargebackAmount"
                          align="right"
                          {...sp}
                          className="px-3 text-right"
                        />
                        <SortHeader
                          label="Coverage"
                          k="owedCoverage"
                          align="right"
                          {...sp}
                          className="px-5 text-right"
                        />
                      </>
                    );
                  })()}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
                {periodSort.sorted.map((p) => (
                  <tr key={p.period}>
                    <td className="px-5 py-2 font-medium">{p.period}</td>
                    <td className="px-3 text-right tabular-nums text-neutral-500">
                      {p.memberCount.toLocaleString()}
                    </td>
                    <td className="px-3 text-right font-medium tabular-nums">
                      {formatCurrency(p.commissionOwed)}
                    </td>
                    <td className="px-3 text-right tabular-nums text-neutral-500">
                      {formatCurrency(p.commissionAtRisk)}
                    </td>
                    <td className="px-3 text-right tabular-nums text-neutral-500">
                      {p.chargebackAmount > 0 ? `-${formatCurrency(p.chargebackAmount)}` : '—'}
                    </td>
                    <td className="px-5 text-right tabular-nums text-neutral-500">
                      {pct(p.owedCoverage)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

const GRADE_TONE: Record<Scorecard['grade'], string> = {
  A: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  B: 'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300',
  C: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  D: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  F: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

/** Carrier scorecards — worst grade first, so the carriers to chase are up top. */
function CarrierScorecards({ rows }: { rows: Scorecard[] }) {
  return (
    <Card className="p-0">
      <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
        <span className="flex items-center gap-1 text-sm font-semibold">
          Carrier scorecards
          <InfoTip title="Carrier scorecards">
            The grade scores carrier <b>behavior</b>: reliability (a statement every active month,
            60%) + cleanliness (statements that don't look incomplete, 40%). The money columns —
            owed, chargebacks, drop rate — are shown alongside so you can see who underpays or claws
            back the most. Worst grade first: the carriers to lean on.
          </InfoTip>
        </span>
        <span className="text-xs text-neutral-400">
          {rows[0]?.periodsExpected ?? 0} active period(s)
        </span>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr className="border-b border-neutral-200 dark:border-neutral-800">
              <th className="px-5 py-2">Carrier</th>
              <th className="px-3 py-2 text-center">Grade</th>
              <th className="px-3 py-2 text-right">Statements</th>
              <th className="px-3 py-2 text-right">Owed</th>
              <th className="px-3 py-2 text-right">Owed / member</th>
              <th className="px-3 py-2 text-right">Chargebacks</th>
              <th className="px-5 py-2 text-right">Drop rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
            {rows.map((c) => (
              <tr key={c.carrierId}>
                <td className="px-5 py-2 font-medium">
                  {c.carrierName ?? c.carrierId.slice(0, 8)}
                </td>
                <td className="px-3 py-2 text-center">
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${GRADE_TONE[c.grade]}`}
                    title={`Score ${c.score}/100`}
                  >
                    {c.grade}
                  </span>
                </td>
                <td
                  className={`px-3 text-right tabular-nums ${c.periodsReported < c.periodsExpected ? 'text-amber-600 dark:text-amber-400' : 'text-neutral-500'}`}
                  title={`${c.qualityFlags} flagged as incomplete`}
                >
                  {c.periodsReported}/{c.periodsExpected}
                </td>
                <td className="px-3 text-right font-medium tabular-nums">
                  {formatCurrency(c.commissionOwed)}
                </td>
                <td className="px-3 text-right tabular-nums text-neutral-500">
                  {formatCurrency(c.owedPerMemberMonth)}
                </td>
                <td className="px-3 text-right tabular-nums text-neutral-500">
                  {c.chargebackAmount > 0 ? `-${formatCurrency(c.chargebackAmount)}` : '—'}
                </td>
                <td className="px-5 text-right tabular-nums text-neutral-500">{c.dropRatePct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Preset({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-neutral-300 px-3 py-1 text-xs font-medium text-neutral-600 transition hover:border-emerald-400 hover:text-emerald-700 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-emerald-600/60"
    >
      {label}
    </button>
  );
}

function BigStat({
  label,
  value,
  note,
  tone,
  info,
  badge,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'emerald' | 'amber' | 'rose';
  info?: ReactNode;
  badge?: ReactNode;
}) {
  const color =
    tone === 'emerald'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'amber'
        ? 'text-amber-600 dark:text-amber-400'
        : tone === 'rose'
          ? 'text-rose-600 dark:text-rose-400'
          : 'text-neutral-800 dark:text-neutral-100';
  return (
    <Card className="p-4">
      <div className="flex items-center gap-1 text-xs text-neutral-500">
        {label}
        {info && <InfoTip title={label}>{info}</InfoTip>}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className={`text-2xl font-semibold tabular-nums ${color}`}>{value}</span>
        {badge}
      </div>
      {note && <div className="mt-1 text-xs text-neutral-400">{note}</div>}
    </Card>
  );
}

function MiniStat({ label, value, info }: { label: string; value: string; info?: ReactNode }) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1 text-xs text-neutral-500">
        {label}
        {info && <InfoTip title={label}>{info}</InfoTip>}
      </div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </Card>
  );
}
